import React from 'react';

const SEGMENT_COLORS = {
  voip:       { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', dot: '#f59e0b' },
  kubernetes: { bg: '#ebf4ff', border: '#bee3f8', text: '#1a365d', dot: '#3182ce' },
  database:   { bg: '#f0fff4', border: '#9ae6b4', text: '#1c4532', dot: '#38a169' },
  web:        { bg: 'var(--accent-soft)', border: 'var(--color-accent-100)', text: 'var(--accent-ink)', dot: 'var(--accent)' },
  network:    { bg: '#fff5f5', border: '#feb2b2', text: '#742a2a', dot: '#e53e3e' },
  storage:    { bg: '#fffaf0', border: '#fbd38d', text: '#7b341e', dot: '#dd6b20' },
  unassigned: { bg: '#f7fafc', border: '#e2e8f0', text: '#4a5568', dot: '#a0aec0' },
};

export function segmentStyle(seg) {
  return SEGMENT_COLORS[seg?.toLowerCase()] || SEGMENT_COLORS.unassigned;
}

export function StatusBadge({ status }) {
  const color = (() => {
    const s = (status || '').toLowerCase();
    if (s === 'active' || s === 'ready') return '#38a169';
    if (s === 'failed') return '#e53e3e';
    if (s === 'provisioning' || s === 'installing' || s === 'connecting') return '#d69e2e';
    return '#a0aec0';
  })();
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      background: color + '22', color, fontWeight: 600, fontSize: 12,
    }}>{status || 'unknown'}</span>
  );
}

export function RoleBadge({ role }) {
  const isCP = role === 'CONTROL_PLANE';
  const color = isCP ? '#6b46c1' : '#2b6cb0';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      background: color + '22', color, fontWeight: 600, fontSize: 12,
    }}>{isCP ? 'Control Plane' : 'Worker'}</span>
  );
}

export function SegmentPill({ segment }) {
  const s = segmentStyle(segment);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
      {segment || 'unassigned'}
    </span>
  );
}

export function SidebarItem({ active, onClick, label, count, color }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '7px 12px', borderRadius: 6, cursor: 'pointer', marginBottom: 2,
      background: active ? color + '18' : 'transparent',
      borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
      transition: 'all 0.15s',
    }}>
      <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? color : 'var(--ink-2)' }}>
        {label}
      </span>
      <span style={{
        background: active ? color + '22' : 'var(--surface-2)', color: active ? color : 'var(--muted)',
        borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600,
      }}>{count}</span>
    </div>
  );
}

// Baremetal server list grouped by segment
export function BaremetalServerList({
  serversBySegment,
  sshResult,
  sshTesting,
  onEdit,
  onDelete,
  onTestSSH,
  onGenerateAgentToken,
}) {
  return (
    <>
      {Object.entries(serversBySegment).map(([seg, segServers]) => {
        const sc = segmentStyle(seg);
        return (
          <div key={seg} style={{ marginBottom: 28 }}>
            {/* Segment header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0,
              padding: '10px 16px', background: sc.bg,
              borderRadius: '8px 8px 0 0', border: `1px solid ${sc.border}`, borderBottom: 'none',
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />
              <span style={{ fontWeight: 700, fontSize: 14, color: sc.text }}>
                {seg.charAt(0).toUpperCase() + seg.slice(1)}
              </span>
              <span style={{
                background: sc.border, color: sc.text, borderRadius: 12,
                padding: '1px 8px', fontSize: 12, fontWeight: 600,
              }}>{segServers.length} server{segServers.length !== 1 ? 's' : ''}</span>
            </div>

            <div style={{ border: `1px solid ${sc.border}`, borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    {['Name', 'IP Address', 'Hostname', 'Connection', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {segServers.map((sv, i) => (
                    <React.Fragment key={sv.id}>
                      <tr style={{ borderBottom: (sshResult[sv.id] || sv.server_metrics) ? 'none' : (i < segServers.length - 1 ? '1px solid var(--border)' : 'none'), background: 'var(--surface)' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--ink-2)', fontSize: 13 }}>{sv.name}</td>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-2)' }}>{sv.ip_address || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-2)' }}>{sv.hostname || '—'}</td>
                        <td style={{ padding: '10px 14px', minWidth: 140 }}>
                          <ConnectionBadge server={sv} />
                        </td>
                        <td style={{ padding: '10px 14px' }}><StatusBadge status={sv.status} /></td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <button className="btn btn-sm btn-secondary" onClick={() => onEdit(sv)}>Edit</button>
                            {sv.connection_type !== 'agent' && (
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => onTestSSH(sv)}
                                disabled={sshTesting[sv.id] === 'testing'}
                                style={{
                                  background: sshTesting[sv.id] === 'ok' ? 'var(--color-success-50)' : sshTesting[sv.id] === 'fail' ? 'var(--color-danger-50)' : undefined,
                                  color:      sshTesting[sv.id] === 'ok' ? 'var(--color-success-700)' : sshTesting[sv.id] === 'fail' ? 'var(--color-danger-700)' : undefined,
                                }}
                              >
                                {sshTesting[sv.id] === 'testing' ? 'Testing…' : sshTesting[sv.id] === 'ok' ? '✓ OK' : sshTesting[sv.id] === 'fail' ? '✗ SSH' : 'Test SSH'}
                              </button>
                            )}
                            {sv.connection_type === 'agent' ? (
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => onGenerateAgentToken(sv, sv.agent_scope || 'read')}
                                title="Show install command / rotate token"
                                style={{ color: 'var(--brand)' }}
                              >📋 Install</button>
                            ) : (
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => onGenerateAgentToken(sv, 'read')}
                                title="Switch to agent — no SSH key needed"
                                style={{ color: 'var(--color-success-700)' }}
                              >+ Agent</button>
                            )}
                            <button className="btn btn-sm btn-secondary" onClick={() => onDelete(sv.id)} style={{ color: 'var(--color-danger-500)' }}>Del</button>
                          </div>
                        </td>
                      </tr>
                      {/* Metrics row — only for agent servers with data */}
                      {sv.connection_type === 'agent' && sv.server_metrics && (
                        <tr style={{ borderBottom: sshResult[sv.id] ? 'none' : (i < segServers.length - 1 ? '1px solid var(--border)' : 'none') }}>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <ServerMetricsRow server={sv} />
                          </td>
                        </tr>
                      )}
                      {sshResult[sv.id] && sv.connection_type !== 'agent' && (
                        <tr style={{ borderBottom: i < segServers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <td colSpan={6} style={{ padding: '0 14px 8px', background: sshResult[sv.id].ok ? 'var(--color-success-50)' : 'var(--color-danger-50)' }}>
                            <div style={{ fontSize: 12, padding: '6px 10px', borderRadius: 4, border: `1px solid ${sshResult[sv.id].ok ? 'var(--color-success-500)' : 'var(--color-danger-500)'}`, color: sshResult[sv.id].ok ? 'var(--color-success-700)' : 'var(--color-danger-700)', fontFamily: 'monospace' }}>
                              {sshResult[sv.id].ok
                                ? <><strong>✓ Connected</strong> ({sshResult[sv.id].latency_ms}ms) — {sshResult[sv.id].output?.split('\n')[0]}</>
                                : <><strong>✗ {sshResult[sv.id].error}</strong></>
                              }
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}

// K8s cluster nodes list
export function KubernetesNodeList({
  clusters,
  clusterNodes,
  activeSegment,
  servers,
  sshResult,
  sshTesting,
  onTestSSH,
  onGenerateAgentToken,
  onInstallAgentOnNode,
}) {
  return (
    <>
      {clusters
        .filter(cl => activeSegment === 'all' || activeSegment === 'kubernetes' || activeSegment === `k8s-${cl.id}`)
        .map(cluster => {
          const nodes = clusterNodes[cluster.id] || [];
          return (
            <div key={cluster.id} style={{ marginBottom: 24 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0,
                padding: '10px 16px', background: 'var(--brand-soft)',
                borderRadius: '8px 8px 0 0', border: '1px solid var(--color-brand-200)', borderBottom: 'none',
              }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--brand)' }}>{cluster.name}</span>
                <span style={{ background: 'var(--color-brand-200)', color: 'var(--brand)', borderRadius: 12, padding: '1px 8px', fontSize: 12, fontWeight: 600 }}>{cluster.environment}</span>
                <StatusBadge status={cluster.status} />
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--brand)' }}>
                  {nodes.length} node{nodes.length !== 1 ? 's' : ''}
                </span>
              </div>
              {nodes.length === 0 ? (
                <div style={{ padding: 16, background: 'var(--surface)', border: '1px solid var(--color-brand-200)', borderRadius: '0 0 8px 8px', color: 'var(--muted)', fontSize: 13 }}>
                  No nodes found. Sync nodes from the cluster detail page.
                </div>
              ) : (
                <div style={{ border: '1px solid var(--color-brand-200)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                        {['Node Name', 'Role', 'Internal IP', 'External IP', 'OS', 'Kubelet', 'Status', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {nodes.map((node, i) => (
                        <React.Fragment key={node.id}>
                          <tr style={{ borderBottom: sshResult[node.server_id] ? 'none' : (i < nodes.length - 1 ? '1px solid var(--border)' : 'none'), background: 'var(--surface)' }}>
                            <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--ink-2)', fontSize: 13 }}>{node.node_name || node.label}</td>
                            <td style={{ padding: '10px 14px' }}><RoleBadge role={node.role} /></td>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-2)' }}>{node.internal_ip || '—'}</td>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-2)' }}>{node.external_ip || '—'}</td>
                            <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--muted)' }}>{node.os_image || '—'}</td>
                            <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--muted)' }}>{node.kubelet_version || '—'}</td>
                            <td style={{ padding: '10px 14px' }}><StatusBadge status={node.status} /></td>
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                              {node.server_id ? (
                                <>
                                  {(() => {
                                    const srv = servers.find(s => s.id === node.server_id);
                                    const isAgentNode = srv?.connection_type === 'agent';
                                    return isAgentNode ? (
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => onGenerateAgentToken(srv, srv.agent_scope || 'read')}
                                        title="Show install command"
                                        style={{ color: 'var(--brand)' }}
                                      >📋 Install</button>
                                    ) : (
                                      <>
                                        <button
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => onTestSSH({ id: node.server_id })}
                                          disabled={sshTesting[node.server_id] === 'testing'}
                                          style={{
                                            background: sshTesting[node.server_id] === 'ok' ? 'var(--color-success-50)' : sshTesting[node.server_id] === 'fail' ? 'var(--color-danger-50)' : undefined,
                                            color:      sshTesting[node.server_id] === 'ok' ? 'var(--color-success-700)' : sshTesting[node.server_id] === 'fail' ? 'var(--color-danger-700)' : undefined,
                                          }}
                                        >
                                          {sshTesting[node.server_id] === 'testing' ? 'Testing…' : sshTesting[node.server_id] === 'ok' ? '✓ SSH OK' : sshTesting[node.server_id] === 'fail' ? '✗ Failed' : 'Test SSH'}
                                        </button>
                                        <button
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => srv && onGenerateAgentToken(srv, 'read')}
                                          title="Switch to agent — no SSH key needed"
                                          style={{ color: 'var(--color-success-700)' }}
                                        >+ Agent</button>
                                      </>
                                    );
                                  })()}
                                </>
                              ) : (
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => onInstallAgentOnNode(node, cluster)}
                                  title="Create server record and get agent install command"
                                  style={{ color: 'var(--color-success-700)' }}
                                >+ Agent</button>
                              )}
                              </div>
                            </td>
                          </tr>
                          {node.server_id && sshResult[node.server_id] && (
                            <tr style={{ borderBottom: i < nodes.length - 1 ? '1px solid var(--border)' : 'none' }}>
                              <td colSpan={8} style={{ padding: '0 14px 8px', background: sshResult[node.server_id].ok ? 'var(--color-success-50)' : 'var(--color-danger-50)' }}>
                                <div style={{ fontSize: 12, padding: '6px 10px', borderRadius: 4, border: `1px solid ${sshResult[node.server_id].ok ? 'var(--color-success-500)' : 'var(--color-danger-500)'}`, color: sshResult[node.server_id].ok ? 'var(--color-success-700)' : 'var(--color-danger-700)', fontFamily: 'monospace' }}>
                                  {sshResult[node.server_id].ok
                                    ? <><strong>✓ Connected</strong> ({sshResult[node.server_id].latency_ms}ms) — {sshResult[node.server_id].output?.split('\n')[0]}</>
                                    : <><strong>✗ {sshResult[node.server_id].error}</strong></>
                                  }
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
    </>
  );
}

// Connection badge shown in the baremetal table (local to this file)
function ConnectionBadge({ server }) {
  const isAgent = server.connection_type === 'agent';
  const lastSeen = server.agent_last_seen ? new Date(server.agent_last_seen) : null;
  const isOnline = lastSeen && (Date.now() - lastSeen.getTime()) < 3 * 60 * 1000;
  const scope = server.agent_scope || 'read';

  if (!isAgent) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 9px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink-2)',
      }}>
        <span style={{ fontSize: 13 }}>🔑</span> SSH
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 9px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        background: isOnline ? 'var(--color-success-50)' : 'var(--bg)',
        border: `1px solid ${isOnline ? 'var(--color-success-500)' : 'var(--border)'}`,
        color: isOnline ? 'var(--color-success-700)' : 'var(--muted)',
      }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: isOnline ? 'var(--color-success-500)' : 'var(--muted-2)',
          display: 'inline-block', flexShrink: 0,
        }} />
        {isOnline ? 'Agent Online' : 'Agent Offline'}
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        background: scope === 'write' ? 'var(--color-warning-50)' : 'var(--brand-soft)',
        border: `1px solid ${scope === 'write' ? 'var(--color-warning-500)' : 'var(--color-brand-200)'}`,
        color: scope === 'write' ? 'var(--color-warning-700)' : 'var(--brand-strong)',
        width: 'fit-content',
      }}>
        {scope === 'write' ? '✏ write' : '👁 read'}
      </span>
    </div>
  );
}

// Mini bar used inside ServerMetricsRow (local helper)
function MiniBar({ percent, warn = 70, crit = 90 }) {
  const color = percent >= crit ? 'var(--color-danger-500)' : percent >= warn ? 'var(--color-warning-500)' : 'var(--color-success-500)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
        <div style={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color, minWidth: 32, textAlign: 'right' }}>{percent}%</span>
    </div>
  );
}

// Compact metrics panel shown under an agent server row
function ServerMetricsRow({ server }) {
  if (!server.server_metrics) return null;
  let m;
  try { m = JSON.parse(server.server_metrics); } catch { return null; }

  const mem = m.memory || {};
  const load = m.load_avg || {};
  const uptime = m.uptime_seconds ? (() => {
    const s = m.uptime_seconds;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  })() : null;

  const disks = (m.disks || []).slice(0, 3);
  const age = server.server_metrics_at
    ? Math.floor((Date.now() - new Date(server.server_metrics_at).getTime()) / 1000)
    : null;
  const ageStr = age === null ? '' : age < 60 ? `${age}s ago` : age < 3600 ? `${Math.floor(age / 60)}m ago` : `${Math.floor(age / 3600)}h ago`;

  return (
    <div style={{
      padding: '8px 14px 10px',
      background: 'var(--bg)',
      borderTop: '1px solid var(--border)',
      display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start',
    }}>
      {/* CPU */}
      <div style={{ minWidth: 120 }}>
        <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, marginBottom: 3 }}>
          CPU {m.cpu_cores ? `(${m.cpu_cores} cores)` : ''}
        </div>
        <MiniBar percent={m.cpu_percent || 0} />
      </div>
      {/* RAM */}
      <div style={{ minWidth: 130 }}>
        <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, marginBottom: 3 }}>
          RAM {mem.used_mb != null ? `(${Math.round(mem.used_mb / 1024 * 10) / 10}/${Math.round(mem.total_mb / 1024 * 10) / 10} GB)` : ''}
        </div>
        <MiniBar percent={mem.percent || 0} />
      </div>
      {/* Disks */}
      {disks.map(d => (
        <div key={d.mount} style={{ minWidth: 120 }}>
          <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, marginBottom: 3 }}>
            Disk {d.mount} ({d.used_gb}/{d.total_gb} GB)
          </div>
          <MiniBar percent={d.percent || 0} warn={75} crit={90} />
        </div>
      ))}
      {/* Load + Uptime */}
      {(load['1m'] != null || uptime) && (
        <div style={{ fontSize: 11, color: 'var(--ink-2)', display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center' }}>
          {load['1m'] != null && (
            <span>Load: <strong>{load['1m']}</strong> / {load['5m']} / {load['15m']}</span>
          )}
          {uptime && <span>Up: <strong>{uptime}</strong></span>}
        </div>
      )}
      {/* Timestamp */}
      {ageStr && (
        <div style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted-2)', alignSelf: 'center' }}>
          {ageStr}
        </div>
      )}
    </div>
  );
}
